import React, { useEffect, useState, useCallback } from 'react';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import * as api from '../lib/api';
import { formatCurrency } from '../lib/format';
import { Search, Link2, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function AnalisiCGPartitari() {
  const YEAR = 2025;
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selAccount, setSelAccount] = useState(null);
  const [entries, setEntries] = useState({ items: [], total: 0 });
  const [onlyIc, setOnlyIc] = useState(false);
  const [icSummary, setIcSummary] = useState(null);
  const [patrSummary, setPatrSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const codeOf = (id) => companies.find((c) => c.id === id)?.code || id;

  const boot = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [comp, ic, patr] = await Promise.all([api.cgaCompanies(), api.cgaPartitariIcSummary(YEAR), api.cgaPatrimonialeSummary(YEAR)]);
      setCompanies(comp);
      setIcSummary(ic);
      setPatrSummary(patr);
      if (comp.length) setCompanyId((prev) => prev || comp[0].id);
    } catch (e) { setErr(api.apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { boot(); }, [boot]);

  useEffect(() => {
    if (!companyId) return;
    setSelAccount(null); setEntries({ items: [], total: 0 });
    api.cgaPartitariAccounts({ company_id: companyId, year: YEAR }).then(setAccounts).catch(() => setAccounts([]));
  }, [companyId]);

  const loadEntries = useCallback((accountCode, ic) => {
    api.cgaPartitariEntries({ company_id: companyId, account_code: accountCode, only_ic: ic, year: YEAR, limit: 500 })
      .then(setEntries).catch(() => setEntries({ items: [], total: 0 }));
  }, [companyId]);

  function pickAccount(a) { setSelAccount(a); loadEntries(a.account_code, onlyIc); }
  function toggleIc(v) { setOnlyIc(v); if (selAccount) loadEntries(selAccount.account_code, v); }

  if (loading) return <div><PageHeader title="Partitari" /><LoadingTable /></div>;
  if (err) return <div><PageHeader title="Partitari" /><ErrorState message={err} onRetry={boot} /></div>;

  return (
    <div data-testid="cga-partitari-page">
      <PageHeader
        title="Partitari — Drill-down conti"
        description="Esplora le singole scritture di ogni conto e le partite intercompany. I partitari non alimentano il consolidato: servono per drill-down ed elisioni."
      />

      {/* Riepilogo Intercompany */}
      {icSummary && (
        <Card className="p-4 mb-4" data-testid="ic-summary-card">
          <div className="flex items-center gap-2 mb-3"><Link2 className="h-4 w-4" /><h3 className="text-sm font-semibold">Riconciliazione intercompany {YEAR}</h3>
            <Badge variant="outline" className="ml-auto">Totale IC: {formatCurrency(icSummary.ic_total)}</Badge>
          </div>
          {icSummary.reconciliation.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna partita intercompany rilevata nei partitari.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40"><tr>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Coppia</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">A → B</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">B → A</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Differenza</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Quadratura</th>
                </tr></thead>
                <tbody>
                  {icSummary.reconciliation.map((r, i) => (
                    <tr key={i} className="border-t" data-testid="ic-recon-row">
                      <td className="p-2 font-medium">{r.a} ↔ {r.b}</td>
                      <td className="p-2 text-right tabular-nums">{formatCurrency(r.a_to_b)}</td>
                      <td className="p-2 text-right tabular-nums">{formatCurrency(r.b_to_a)}</td>
                      <td className={`p-2 text-right tabular-nums ${r.squared ? 'text-muted-foreground' : 'text-[hsl(var(--danger))]'}`}>{formatCurrency(r.difference)}</td>
                      <td className="p-2">{r.squared ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" />Quadra</span> : <span className="inline-flex items-center gap-1 text-[hsl(var(--danger))]"><AlertTriangle className="h-4 w-4" />Da verificare</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Riconciliazione Patrimoniale (crediti/debiti infragruppo) */}
      {patrSummary && (
        <Card className="p-4 mb-4" data-testid="patr-summary-card">
          <div className="flex items-center gap-2 mb-3"><Link2 className="h-4 w-4" /><h3 className="text-sm font-semibold">Riconciliazione patrimoniale infragruppo {YEAR} — crediti / debiti</h3>
            <div className="ml-auto flex gap-2">
              <Badge variant="outline">Crediti IC: {formatCurrency(patrSummary.totals.crediti_ic)}</Badge>
              <Badge variant="outline">Debiti IC: {formatCurrency(patrSummary.totals.debiti_ic)}</Badge>
              <Badge variant="secondary">Eliminabile: {formatCurrency(patrSummary.totals.eliminabile)}</Badge>
            </div>
          </div>
          {patrSummary.pairs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna partita patrimoniale infragruppo rilevata.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40"><tr>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Creditore</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Debitore</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Crediti</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Debiti</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Differenza</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Quadratura</th>
                </tr></thead>
                <tbody>
                  {patrSummary.pairs.map((r, i) => (
                    <tr key={i} className="border-t" data-testid="patr-recon-row">
                      <td className="p-2 font-medium">{r.creditor}</td>
                      <td className="p-2 font-medium">{r.debtor}</td>
                      <td className="p-2 text-right tabular-nums">{formatCurrency(r.crediti)}</td>
                      <td className="p-2 text-right tabular-nums">{formatCurrency(r.debiti)}</td>
                      <td className={`p-2 text-right tabular-nums ${r.squared ? 'text-muted-foreground' : 'text-[hsl(var(--danger))]'}`}>{formatCurrency(r.difference)}</td>
                      <td className="p-2">{r.squared ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" />Quadra</span> : <span className="inline-flex items-center gap-1 text-[hsl(var(--danger))]"><AlertTriangle className="h-4 w-4" />Da verificare</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">Controparte dedotta dalla descrizione dei conti infragruppo (es. "CREDITI V/FIER O&amp;M SRL"). Elisioni solo di riconciliazione: non impattano il conto economico consolidato.</p>
        </Card>
      )}

      {/* Selettore società */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-72"><Label>Società</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="partitari-company-select"><SelectValue placeholder="Seleziona società" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Badge variant="outline" className="mb-1.5">Anno {YEAR}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* Elenco conti */}
        <Card className="p-0 overflow-hidden">
          <div className="p-3 border-b bg-muted/30"><h3 className="text-sm font-semibold">Conti ({accounts.length})</h3></div>
          <div className="max-h-[560px] overflow-y-auto">
            {accounts.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Nessuna scrittura per questa società.</p> : (
              <table className="w-full text-sm">
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.account_code}
                        className={`border-t cursor-pointer hover:bg-muted/40 ${selAccount?.account_code === a.account_code ? 'bg-muted/50' : ''}`}
                        onClick={() => pickAccount(a)} data-testid="partitari-account-row">
                      <td className="p-2">
                        <div className="font-mono text-xs">{a.account_code}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[210px]">{a.account_desc}</div>
                      </td>
                      <td className="p-2 text-right">
                        <div className="tabular-nums text-xs">{formatCurrency(a.total)}</div>
                        {a.ic_total > 0 && <Badge variant="outline" className="mt-0.5 text-[10px]"><Link2 className="h-2.5 w-2.5 mr-0.5" />IC {formatCurrency(a.ic_total)}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        {/* Scritture del conto */}
        <Card className="p-0 overflow-hidden">
          <div className="p-3 border-b bg-muted/30 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              {selAccount ? <>Scritture · <span className="font-mono">{selAccount.account_code}</span> <span className="text-muted-foreground font-normal">{selAccount.account_desc}</span></> : 'Seleziona un conto'}
            </h3>
            {selAccount && (
              <div className="flex items-center gap-2 text-xs"><Label htmlFor="only-ic" className="text-xs text-muted-foreground">Solo intercompany</Label>
                <Switch id="only-ic" checked={onlyIc} onCheckedChange={toggleIc} data-testid="only-ic-switch" />
              </div>
            )}
          </div>
          {!selAccount ? (
            <div className="p-10 text-center text-sm text-muted-foreground"><Search className="h-5 w-5 mx-auto mb-2 opacity-50" />Scegli un conto dall'elenco a sinistra per vedere il dettaglio delle scritture.</div>
          ) : entries.items.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Nessuna scrittura {onlyIc ? 'intercompany ' : ''}per questo conto.</p>
          ) : (
            <div className="max-h-[560px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="partitari-entries-table">
                <thead className="bg-muted/40 sticky top-0"><tr>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Data</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Doc.</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Causale</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Controparte</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Importo</th>
                </tr></thead>
                <tbody>
                  {entries.items.map((t) => (
                    <tr key={t.id} className={`border-t ${t.is_intercompany ? 'bg-[hsl(var(--success)/0.06)]' : ''}`} data-testid="partitari-entry-row">
                      <td className="p-2 whitespace-nowrap text-xs">{t.data_reg}</td>
                      <td className="p-2 text-xs font-mono">{t.num_doc}</td>
                      <td className="p-2 text-xs">{t.causale}</td>
                      <td className="p-2 text-xs">
                        {t.counterpart_name || '—'}
                        {t.is_intercompany && <Badge variant="secondary" className="ml-1.5 text-[10px]"><Link2 className="h-2.5 w-2.5 mr-0.5" />{t.counterpart_company_code}</Badge>}
                      </td>
                      <td className="p-2 text-right tabular-nums">{formatCurrency(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
